import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Autocomplete, Button } from '@avangard/ui/core';
import { Checkbox, FormControlLabel, FormGroup, withStyles } from '@material-ui/core';

import { routes } from '@config/routes';
import { createRouteUrl, useNavigate } from '@lib/routing';
import { useRegions, useLazySchools, useLazyLocations } from '@modules/shared/hooks';
import { useEnqueueStacks } from '@modules/layout/hooks';
import { FormStyled } from '@modules/layout/styled';
import { ExtendedFormik, Form } from '@modules/layout/organisms';
import { useCurrentUser } from '@modules/user/hooks';
import { SchoolCollectionRequest } from '@modules/schools/requests';
import { useSchoolsTableFiltersQuery } from '@modules/schools/hooks';
import { GetSchoolCollectionQuery } from '@modules/schools/graphql';
import { useCurrentLap } from '@modules/lap/hooks';
import { AttachSchoolFormValues, AttachSchoolRequest } from '@modules/schools/requests';
import { CreateLapAccessMutation } from '@modules/lap/graphql';
import { UserGrantActionToken, UserGrantCategoryToken } from '@modules/types/graphql';

import type { RegionEntity, LocationEntity } from '@modules/shared/entities';
import type { SchoolEntity } from '@modules/schools/entities';
import type { LapAccessEntity } from '@modules/lap/entities';
import type {
    CreateLapAccessMutationType,
    CreateLapAccessMutationVariables,
} from '@modules/types/graphql';

type SchoolAttachFormProps = {
    canGet: boolean;
    onCancel: () => void;
};

const StyledForm = styled(Form)`
    width: 100%;

    > * {
        &:not(:last-of-type) {
            margin-bottom: 24px;
        }
    }
`;

const StyledFormGroup = withStyles({
    root: {
        marginTop: -8,
        marginInlineEnd: -4,
        marginBottom: '12px!important',

        '& > *': {
            width: '100%',
        },
    },
})(FormGroup);

const FormRowSubmit = styled(FormStyled.FormRowSubmit)`
    justify-content: flex-end;
`;

const SchoolAttachForm = (props: SchoolAttachFormProps) => {
    const { canGet, onCancel } = props;

    const { t } = useTranslation(['common', 'errors', 'schools']);

    const navigate = useNavigate();

    const { enqueueSuccess, enqueueError } = useEnqueueStacks();

    const { currentUser } = useCurrentUser();
    const { schoolGrants } = currentUser.can({
        [UserGrantCategoryToken.school]: [UserGrantActionToken.list],
    });

    const { filters } = useSchoolsTableFiltersQuery();

    const { regions, loading: regionsLoading } = useRegions();
    const [getLocations, { locations, count: locationsCount, loading: locationsLoading }] =
        useLazyLocations();

    const [getSchools, { schools, count: schoolsCount, loading: schoolsLoading }] =
        useLazySchools();

    const { currentLap } = useCurrentLap();

    const [createLapAccess] = useMutation<
        CreateLapAccessMutationType,
        CreateLapAccessMutationVariables
    >(CreateLapAccessMutation);

    const getOptionLabel = React.useCallback(
        (field: string) =>
            (option: any | null): string =>
                option?.[field] || '-',
        [],
    );

    return (
        <ExtendedFormik<AttachSchoolFormValues>
            validationSchema={yup.object({
                location: yup.mixed().required(t('errors:validations.required') ?? ''),
                school: yup.mixed().required(t('errors:validations.required') ?? ''),
            })}
            initialValues={{
                region: null,
                location: null,
                school: null,
                neededCopyFromLap: false,
                fromLap: null,
            }}
            onSubmit={async values => {
                try {
                    let options: Parameters<typeof createLapAccess>[0] = {
                        variables: {
                            item: new AttachSchoolRequest(values, { lap: currentLap }),
                        },
                    };

                    if (schoolGrants.canList) {
                        options = {
                            ...options,
                            refetchQueries: [
                                {
                                    query: GetSchoolCollectionQuery,
                                    variables: { args: new SchoolCollectionRequest(filters) },
                                },
                            ],
                            awaitRefetchQueries: true,
                        };
                    }

                    const { data: createLapAccessData } = await createLapAccess(options);

                    const schoolId = createLapAccessData?.createLapAccess?.targetId;

                    if (!schoolId) {
                        enqueueError(t('schools:notifiers.add_school.error'));
                        return;
                    }

                    enqueueSuccess(t('schools:notifiers.add_school.success'));
                    onCancel();

                    if (canGet) {
                        navigate(createRouteUrl(routes.schoolsEditPage, { id: schoolId }));
                    }
                } catch (e) {
                    console.error(e);
                    throw e;
                }
            }}
        >
            {formikProps => {
                const { values, errors, handleChange, setFieldValue, isSubmitting } = formikProps;

                React.useEffect(() => {
                    if (!values.region?.id) {
                        return;
                    }

                    getLocations({ variables: { regionId: values.region.id } });
                }, [values.region?.id]);

                React.useEffect(() => {
                    setFieldValue('school', null);
                    setFieldValue('fromLap', null);

                    if (!values.location?.id) {
                        return;
                    }

                    getSchools({
                        variables: {
                            args: {
                                filter: {
                                    inAllLaps: true,
                                    location: {
                                        id: values.location?.id,
                                    },
                                },
                                page: { all: true },
                            },
                        },
                    });
                }, [values.location?.id]);

                React.useEffect(() => {
                    setFieldValue('fromLap', null);
                }, [values.school?.id]);

                const lapAccessList = React.useMemo(
                    () => values.school?.lapAccessList,
                    [values.school],
                );

                const handleChangeAutocomplete =
                    (id: string) => (_: React.ChangeEvent<{}>, value: any) => {
                        setFieldValue(id, value);

                        if (id === 'region') {
                            setFieldValue('location', null);
                        }
                    };

                return (
                    <StyledForm>
                        <Autocomplete<RegionEntity | null, false, false, false>
                            required
                            fullWidth
                            id='region'
                            label={t('common:forms.labels.region') ?? ''}
                            placeholder={t('common:forms.placeholder.choose_region') ?? ''}
                            options={regions}
                            value={values.region}
                            error={!!errors.region}
                            helperText={errors.region}
                            disabled={regionsLoading}
                            onChange={handleChangeAutocomplete('region')}
                            getOptionLabel={getOptionLabel('name')}
                        />

                        <Autocomplete<LocationEntity | null, false, false, false>
                            required
                            fullWidth
                            id='location'
                            label={t('common:forms.labels.location') ?? ''}
                            placeholder={t('common:forms.placeholder.choose_location') ?? ''}
                            options={locations}
                            value={values.location}
                            error={!!errors.location}
                            helperText={errors.location}
                            disabled={!values.region || locationsCount === 0 || locationsLoading}
                            onChange={handleChangeAutocomplete('location')}
                            getOptionLabel={getOptionLabel('name')}
                        />

                        <Autocomplete<SchoolEntity | null, false, false, false>
                            required
                            fullWidth
                            id='school'
                            label={t('common:forms.labels.school_short_name') ?? ''}
                            placeholder={
                                t('common:forms.placeholder.choose_school_short_name') ?? ''
                            }
                            options={schools}
                            value={values.school}
                            error={!!errors.school}
                            helperText={errors.school}
                            disabled={!values.location || schoolsLoading || schoolsCount === 0}
                            onChange={handleChangeAutocomplete('school')}
                            getOptionLabel={getOptionLabel('shortName')}
                        />

                        {!!lapAccessList && lapAccessList.length > 0 && (
                            <StyledFormGroup row>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name='neededCopyFromLap'
                                            checked={values.neededCopyFromLap}
                                            onChange={handleChange}
                                        />
                                    }
                                    label={t('schools:form.labels.needed_copy_from_lap')}
                                />
                            </StyledFormGroup>
                        )}

                        {!!lapAccessList &&
                            lapAccessList.length > 0 &&
                            values.neededCopyFromLap && (
                                <Autocomplete<LapAccessEntity | null, false, false, false>
                                    fullWidth
                                    id='fromLap'
                                    label={t('common:forms.labels.lap') ?? ''}
                                    placeholder={t('common:forms.placeholder.choose_lap') ?? ''}
                                    options={lapAccessList}
                                    value={values.fromLap}
                                    error={!!errors.fromLap}
                                    helperText={errors.fromLap}
                                    getOptionLabel={option =>
                                        option?.lap?.getFormattedName() ?? '-'
                                    }
                                    onChange={handleChangeAutocomplete('fromLap')}
                                />
                            )}

                        <FormRowSubmit>
                            <Button variant='outlined' disabled={isSubmitting} onClick={onCancel}>
                                {t('common:actions.cancel')}
                            </Button>

                            <Button type='submit' loading={isSubmitting}>
                                {t('common:actions.add')}
                            </Button>
                        </FormRowSubmit>
                    </StyledForm>
                );
            }}
        </ExtendedFormik>
    );
};

export { SchoolAttachForm };
